import { Directive, inject } from '@angular/core';
import { TaskListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/task-list.response';
import { TaskState } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/task-state.enum';
import { GardenTodo, GardenTodoStore } from '../../@services/data/garden-todo.store';
import { todoCount } from '../../utils/garden-todo.util';
import { UndoableList } from '../../ui-components/undo-snackbar/undoable-list';

/**
 * Cocher un geste depuis une liste de tâches : « Mes tâches » et l'accueil.
 *
 * Un geste fait ou ignoré s'écrit tout de suite (« Annuler » le rouvre) ; une
 * action différée (`commit`) ne part qu'à la fermeture du message (voir
 * `UndoableList`). Une ligne cochée n'ouvre pas la feuille.
 */
@Directive()
export abstract class TaskChecking extends UndoableList {
  protected readonly store = inject(GardenTodoStore);

  today = new Date();
  /** La tâche dont la feuille est ouverte. */
  sheetTask: TaskListResponse | null = null;

  onTaskChecked(task: TaskListResponse) {
    if (!this.checked.has(task.uid)) this.closeTask(task, TaskState.DONE, 'tasks.undo.done');
  }

  /** La feuille d'un geste ; une ligne déjà cochée attend son message, pas une feuille. */
  openSheet(task: TaskListResponse) {
    if (!this.checked.has(task.uid)) this.sheetTask = task;
  }

  /** « C'est fait » dans la feuille : comme cocher la ligne. */
  onSheetDone() {
    const task = this.closeSheet();
    if (task) this.onTaskChecked(task);
  }

  /** « Ignorer cette fois » : pour cette occurrence seulement (décision 11). */
  onSheetSkipped() {
    const task = this.closeSheet();
    if (task) this.closeTask(task, TaskState.SKIP, 'tasks.undo.skipped');
  }

  /**
   * Au retour au premier plan, le jour est relu : un écran laissé ouvert passé
   * minuit range ses tâches et compte ses jours sur le nouveau.
   */
  protected override onVisible() {
    this.today = new Date();
    this.countChanged();
  }

  /** Les tâches qui n'ont pas encore quitté la liste. */
  protected notGone(tasks: TaskListResponse[]): TaskListResponse[] {
    return tasks.filter((task) => !this.gone.has(task.uid));
  }

  /** Ce qui reste à faire : le compte de « Mes tâches », sans les lignes cochées ni parties. */
  protected countLeft(todo: GardenTodo): number {
    return todoCount(
      {
        tasks: this.notGone(todo.tasks).filter((task) => !this.checked.has(task.uid)),
        plantActions: todo.plantActions.filter((action) => !this.gone.has(action.plant.uid) && !this.checked.has(action.plant.uid)),
      },
      this.today,
    );
  }

  protected closeSheet(): TaskListResponse | null {
    const task = this.sheetTask;
    this.sheetTask = null;
    return task;
  }

  /**
   * Un geste fait ou ignoré s'écrit tout de suite ; « Annuler » le rouvrira.
   * Le store fait attendre la réouverture derrière l'écriture, et n'écrit que
   * le dernier choix : recocher pendant une écriture lente reste coché. Une
   * réouverture refusée ne compte que si le geste s'était écrit : sinon la
   * tâche n'a jamais quitté TODO.
   */
  protected closeTask(task: TaskListResponse, state: TaskState, message: string) {
    this.settle();
    this.check(task.uid);
    const written = this.store.setTaskState(task.uid, state);
    this.pendingUndo = {
      key: task.uid,
      message,
      revert: () =>
        this.store.setTaskState(task.uid, TaskState.TODO).catch((e) =>
          written.then(
            (wasWritten) => (wasWritten ? Promise.reject(e) : undefined),
            () => undefined,
          ),
        ),
    };
    written.catch(() => this.restore(task.uid));
  }
}
